/**
 * Drawer Changelog — What's New
 *
 * Slide-over affichant les notes de version groupees par release.
 */

import { useState } from 'react';
import { Sparkles, Wrench, Bug, ChevronDown } from 'lucide-react';
import { Drawer } from '@/components/ui';
import type { ChangelogEntry } from '@bstock/shared';
import { useChangelog } from '../hooks/useChangelog';

interface ChangelogDrawerProps {
  open: boolean;
  onClose: () => void;
}

function CategorySection({
  icon: Icon,
  label,
  items,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  items: string[];
  color: string;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mt-3">
      <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${color} mb-1.5`}>
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <ul className="space-y-1 ml-5">
        {items.map((item, i) => (
          <li key={i} className="text-sm text-neutral-600 list-disc">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

function VersionEntry({ entry, defaultOpen }: { entry: ChangelogEntry; defaultOpen?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultOpen ?? false);

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-primary-100 text-primary-700">
            v{entry.version}
          </span>
          <span className="text-sm font-medium text-dark-500">{entry.title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-400">{entry.date}</span>
          <ChevronDown
            className={`w-4 h-4 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="px-4 py-3">
          {/* Highlights */}
          {entry.highlights.length > 0 && (
            <div className="bg-primary-50 border border-primary-100 rounded-lg px-3 py-2 mb-3">
              <ul className="space-y-1">
                {entry.highlights.map((h, i) => (
                  <li key={i} className="text-sm text-primary-800 flex items-start gap-2">
                    <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary-500" />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Categories */}
          <CategorySection
            icon={Sparkles}
            label="Nouveautes"
            items={entry.categories.features ?? []}
            color="text-emerald-600"
          />
          <CategorySection
            icon={Wrench}
            label="Ameliorations"
            items={entry.categories.improvements ?? []}
            color="text-blue-600"
          />
          <CategorySection
            icon={Bug}
            label="Corrections"
            items={entry.categories.fixes ?? []}
            color="text-orange-600"
          />
        </div>
      )}
    </div>
  );
}

export function ChangelogDrawer({ open, onClose }: ChangelogDrawerProps) {
  const { allEntries, markAsSeen, latestVersion } = useChangelog();

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      markAsSeen();
      onClose();
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      title="Nouveautes"
      description={`Version actuelle : v${latestVersion}`}
      size="default"
    >
      <div className="space-y-3">
        {allEntries.map((entry, index) => (
          <VersionEntry key={entry.version} entry={entry} defaultOpen={index === 0} />
        ))}
      </div>
    </Drawer>
  );
}
